import { useEffect, useState } from 'react';
import {
  Drawer, Box, Button, Typography, Chip, TextField, MenuItem, Stack, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getFinding, updateFindingWorkflow } from '../../../services/securityhubApi';
import { getGuardDutyFinding, updateGDWorkflow } from '../../../services/guarddutyApi';
import { getMacieFinding, updateMacieWorkflow } from '../../../services/macieApi';
import { getInspectorFinding } from '../../../services/inspectorApi';
import { getDetectiveFinding } from '../../../services/detectiveApi';
import { getAccessAnalyzerFinding } from '../../../services/accessAnalyzerApi';
import { engineById } from './engines/registry';
import CspmDetailBody from './CspmDetailBody';
import GuardDutyDetailBody from './GuardDutyDetailBody';
import MacieDetailBody from './MacieDetailBody';
import InspectorDetailBody from './InspectorDetailBody';
import DetectiveDetailBody from './DetectiveDetailBody';
import AccessAnalyzerDetailBody from './AccessAnalyzerDetailBody';
import { severityChipSx, stateChipSx } from './severity';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { CK, FONT_TITLE, FONT_BODY } from '../portalStyles';

const WORKFLOW_STATUSES = ['NEW', 'NOTIFIED', 'SUPPRESSED', 'RESOLVED'];

// GuardDuty surfaces archive/unarchive rather than a workflow-status dropdown:
// NEW/ACTIVE findings are "live" (offer Archive); SUPPRESSED/ARCHIVED are
// archived (offer Unarchive).
const isArchived = (status) => status === 'SUPPRESSED' || status === 'ARCHIVED';

// Inspector's status is AWS-side (ACTIVE | SUPPRESSED | CLOSED), but the unified
// inbox normalizes it to NEW | SUPPRESSED | RESOLVED before a row reaches the
// findings table, so the list and this drawer read the same finding through two
// different vocabularies. One mapper that accepts BOTH is what stops them
// labelling the same finding differently; it lives here, in the shared drawer the
// list already imports, rather than in either engine-specific screen.
export const inspectorStatusLabel = (v) => {
  switch (String(v || '').toUpperCase()) {
    case 'SUPPRESSED':
      return 'Suppressed';
    case 'CLOSED':
    case 'RESOLVED':
    case 'ARCHIVED':
      return 'Closed';
    default:
      return 'Active';
  }
};

// Access Analyzer's status is AWS-side too (ACTIVE | ARCHIVED), and the unified
// inbox normalizes it to NEW | SUPPRESSED before a row reaches the findings table
// — so, exactly like Inspector's above, the list and this drawer read the same
// finding through two different vocabularies and one mapper has to accept BOTH.
// It lives HERE, in the shared drawer, and is imported by FindingsList: that file
// already imports from this one, so defining it there instead would make the two
// modules import each other and the cycle would resolve to `undefined` at module
// evaluation time in whichever direction lost.
//
// RESOLVED gets its own word rather than being folded into "Archived". Prism never
// caches a RESOLVED finding — it means the underlying access was actually removed,
// AWS sets it, and those rows age out — so this arm is unreachable through the
// list, but a hand-built URL or a future ingest path must not have a fixed exposure
// render as "Active".
export const accessAnalyzerStatusLabel = (v) => {
  switch (String(v || '').toUpperCase()) {
    case 'ARCHIVED':
    case 'SUPPRESSED':
      return 'Archived';
    case 'RESOLVED':
      return 'Resolved';
    default:
      return 'Active';
  }
};

// Per-engine detail behaviour, keyed by the engine id the clicked row carries.
//
// This was four independent `isGuardDuty ? … : isMacie ? … : …` chains — one for
// the fetch, one for the body, one for the save action, one for the footer copy —
// so a capability was only wired up if EVERY chain got a new arm. Inspector was
// added to the body chain alone, which made an Inspector finding render the
// Inspector body over data fetched from the CSPM endpoint. A table turns a
// half-wired capability into a missing entry instead of four silent
// fall-throughs.
//
// Every field below is READ by the render, and nothing is declared that the
// render ignores. There is deliberately no `mode: 'readonly' | 'archive' | …`
// enum: a label that nothing enforces is the same silent half-wiring in a new
// costume — an entry could claim to be read-only and still hand out a save
// button. What each engine offers is therefore expressed by which fields exist:
//   fetch        the single-finding endpoint
//   Body         the detail body (see below)
//   archived     absent → the engine has no archive concept
//   mutate       absent → the engine exposes NO mutation, so NO save button is
//                rendered; a disabled button that still looks actionable is
//                worse than no button
//   saveLabel    the save button's text
//   stateLabel   absent → show CSPM's workflowStatus + recordState chip pair
//   footerNote   prose explaining this engine's triage model
//   workflowForm render Security Hub's status + note form instead of that prose
//
// `Body` is pinned rather than left to the registry for every engine that has a
// dedicated one. Not because the payload lacks an `engine` field — all four
// detail DTOs embed NormalizedFinding and do carry it. Pinning buys independence
// from the response: the body is chosen by the ROW the user clicked, which is
// known before the fetch resolves and cannot drift if an endpoint's shape or
// engine tag ever changes. The registry lookup remains the fallback for entries
// that pin nothing.
const ENGINE_DETAIL = {
  cspm: {
    workflowForm: true,
    fetch: getFinding,
    mutate: ({ findingId, workflow, note }) => updateFindingWorkflow([findingId], workflow, note),
    saveLabel: () => 'Update workflow',
  },
  guardduty: {
    fetch: getGuardDutyFinding,
    Body: GuardDutyDetailBody,
    // The GuardDuty single-finding endpoint returns the normalized shape, so
    // workflowStatus is NEW/SUPPRESSED; fall back to the raw `archived` flag for
    // safety.
    archived: (f) => isArchived(f?.workflowStatus) || !!f?.archived,
    mutate: ({ findingId, archived }) => updateGDWorkflow({ findingIds: [findingId], archive: !archived }),
    saveLabel: (archived) => (archived ? 'Unarchive' : 'Archive'),
    // GuardDuty has no workflow status — just an archived/not-archived state.
    stateLabel: (f, archived) => (archived ? 'Archived' : 'Not archived'),
    footerNote: (f, archived) => (archived
      ? 'This finding is archived. Unarchive to move it back to active threats.'
      : 'Archive this finding to remove it from active threats.'),
  },
  macie: {
    fetch: getMacieFinding,
    Body: MacieDetailBody,
    // Macie archive is Prism-local (no AWS-side archive API); the normalized
    // detail exposes it the same way GuardDuty does — workflowStatus ARCHIVED or
    // raw flag.
    archived: (f) => isArchived(f?.workflowStatus) || !!f?.archived,
    mutate: ({ findingId, archived }) => updateMacieWorkflow({ findingIds: [findingId], archive: !archived }),
    saveLabel: (archived) => (archived ? 'Unarchive' : 'Archive in Prism'),
    // Macie archive is Prism-local triage state, not an AWS record state.
    stateLabel: (f, archived) => (archived ? 'Archived (Prism)' : 'Active'),
    footerNote: (f, archived) => (archived
      ? 'This finding is archived in Prism. Unarchive to move it back to active findings. Archiving here is Prism-local — the AWS Macie console is unaffected.'
      : 'Archive this finding in Prism to remove it from active findings. This only affects Prism — the AWS Macie console is unaffected.'),
  },
  inspector: {
    // READ-ONLY BY DESIGN, not unfinished: Inspector's status lives in AWS and the
    // backend exposes no mutation for it — no archive, no suppress, no workflow
    // update. The absent `mutate` and `archived` fields ARE that statement, and
    // the render honours them directly, so it cannot drift from a claim made in
    // prose.
    fetch: getInspectorFinding,
    Body: InspectorDetailBody,
    // `status` is Inspector's own ACTIVE|SUPPRESSED|CLOSED; workflowStatus is
    // Prism's normalization of it. inspectorStatusLabel reads either, so the chip
    // is right whichever the payload carries.
    stateLabel: (f) => inspectorStatusLabel(f?.status || f?.workflowStatus),
    footerNote: () => 'Amazon Inspector owns the state of this finding. Prism mirrors it read-only — suppress or close it in the Inspector console and the change appears here after the next refresh.',
  },
  detective: {
    // READ-ONLY, and the absent fields are the statement: Prism does not carry
    // detective:UpdateInvestigationState, so there is NO mutate, NO archived and
    // therefore no save button at all — a disabled button that still looks
    // actionable is worse than no button.
    fetch: getDetectiveFinding,
    Body: DetectiveDetailBody,
    // recordState is the field that carries ACTIVE|ARCHIVED. `status` on this
    // payload is the ANALYSIS RUN (RUNNING|FAILED|SUCCESSFUL) and must never be
    // read here: a still-running investigation would render as an archived
    // finding, the exact opposite of what running means. The detail body shows
    // the analysis run separately and says so in words.
    stateLabel: (f) => (f?.recordState === 'ARCHIVED' ? 'Archived' : 'Active'),
    footerNote: () =>
      'Investigation state is managed in the Amazon Detective console. Prism reads investigations and does not change them.',
  },
  accessanalyzer: {
    // READ-ONLY BY DESIGN, not unfinished: the finding's status lives in AWS —
    // archiving happens through an archive rule or the Access Analyzer console —
    // and the backend exposes no mutation for it. The absent `mutate` and
    // `archived` fields ARE that statement, and the render honours them directly,
    // so it cannot drift from a claim made in prose.
    //
    // The route is addressed by the AWS finding id, which is what
    // normalizeAccessAnalyzerFinding puts in FindingID — not the synthetic row
    // uuid, which the detail payload also carries as `id`.
    fetch: getAccessAnalyzerFinding,
    Body: AccessAnalyzerDetailBody,
    // `status` is AWS's own ACTIVE|ARCHIVED; workflowStatus is Prism's
    // normalization of it. accessAnalyzerStatusLabel reads either, so the chip is
    // right whichever the payload carries.
    stateLabel: (f) => accessAnalyzerStatusLabel(f?.status || f?.workflowStatus),
    footerNote: () => 'AWS IAM Access Analyzer owns the state of this finding. Prism mirrors it read-only — archive it in the Access Analyzer console, or with an archive rule, and the change appears here after the next refresh.',
  },
};

// FindingDetail renders as a right side-panel (AWS Security Hub / GuardDuty
// console style) rather than a centred modal, so users migrating from the AWS
// console get the familiar slide-in finding panel.
export default function FindingDetail({ findingId, engine, open, onClose, onUpdated }) {
  // An absent or unrecognised engine resolves to CSPM, which is what the
  // standalone CSPM findings list sends — its rows carry no `engine` field.
  const cfg = ENGINE_DETAIL[engine] || ENGINE_DETAIL.cspm;
  const [finding, setFinding] = useState(null);
  const [error, setError] = useState(null);
  const [workflow, setWorkflow] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !findingId) return;
    // Drop the previous finding first: without this, opening a second finding
    // keeps the old title, severity chip and body on screen until the new
    // request resolves — a visible flash of the wrong finding.
    setFinding(null);
    setError(null);
    (async () => {
      try {
        const res = await cfg.fetch(findingId);
        const data = res?.data || null;
        setFinding(data);
        setWorkflow(data?.workflowStatus || 'NEW');
        // An empty payload must not leave the body spinning forever now that
        // `!finding && !error` renders the loading block.
        setError(data ? null : 'Could not load this finding.');
      } catch (e) {
        setError(e.message);
      }
    })();
    // `cfg` is a module-level object looked up by engine id, so its identity only
    // changes when `engine` does — this cannot loop.
  }, [findingId, open, cfg]);

  const archived = cfg.archived ? cfg.archived(finding) : false;

  const save = async () => {
    // Defensive: read-only engines render no save button, so this is unreachable
    // through the UI — but it keeps "no mutate" meaning "no mutation" if a future
    // caller wires a keyboard shortcut or a bulk action to this handler.
    if (!cfg.mutate) return;
    setSaving(true);
    try {
      await cfg.mutate({ findingId, archived, workflow, note });
      setError(null);
      if (onUpdated) onUpdated();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Engine-aware body: the engine's own body when it pins one, otherwise the
  // registered descriptor for whatever engine the payload reports, otherwise CSPM.
  const Body = cfg.Body || engineById(finding?.engine)?.DetailBody || CspmDetailBody;
  const saveLabel = cfg.saveLabel ? cfg.saveLabel(archived) : '';
  // Every engine's detail DTO exposes severity as `severity`.
  const severityLabel = finding?.severity;

  const footerNoteSx = { fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body, mb: 1.5 };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: CK.surface,
        },
      }}
    >
      {/* Sticky header: title + severity / workflow / record-state badges */}
      <Box sx={{ p: 2.5, backgroundColor: CK.pageBg, borderBottom: `1px solid ${CK.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: FONT_TITLE,
              fontWeight: 600,
              fontSize: '1.15rem',
              color: CK.navy,
              lineHeight: 1.3,
              pr: 1,
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            {finding?.title || 'Finding'}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              flexShrink: 0,
              color: CK.body,
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': { backgroundColor: CK.hover, color: CK.navy },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {finding && (
          <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ mt: 1.5, gap: 0.75 }}>
            {/* A finding with NO severity gets the explicit "Unrated" chip in the
                neutral state colour rather than an empty pill — the same word the
                findings list and DetectiveDetailBody use, so the header and the
                body of this very drawer cannot disagree. Only Detective produces
                one (an investigation has no severity until its report has been
                fetched); the other four engines always emit a band, so this is a
                no-op for them. */}
            <Chip
              size="small"
              label={severityLabel || 'Unrated'}
              sx={severityLabel ? severityChipSx(severityLabel) : stateChipSx}
            />
            {cfg.stateLabel ? (
              <Chip size="small" label={cfg.stateLabel(finding, archived)} sx={stateChipSx} />
            ) : (
              <>
                {finding.workflowStatus && <Chip size="small" label={finding.workflowStatus} sx={stateChipSx} />}
                {finding.recordState && <Chip size="small" label={finding.recordState} sx={stateChipSx} />}
              </>
            )}
          </Stack>
        )}
      </Box>

      {/* Scrollable, sectioned body */}
      <Box sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
        {error && <PortalErrorState error={error} />}
        {!finding && !error && <PortalLoading label="Loading finding…" />}
        {finding && <Body finding={finding} />}
      </Box>

      {/* Sticky footer: workflow control + actions */}
      {finding && (
        <Box sx={{ p: 2.5, backgroundColor: CK.pageBg, borderTop: `1px solid ${CK.border}` }}>
          {cfg.workflowForm ? (
            <Box sx={{ display: 'flex', gap: 2, rowGap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <TextField
                select label="Workflow status" value={workflow} size="small"
                onChange={(e) => setWorkflow(e.target.value)}
                sx={{ minWidth: 170 }}
              >
                {WORKFLOW_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField
                label="Note (optional)" value={note} size="small"
                onChange={(e) => setNote(e.target.value)}
                sx={{ flex: '1 1 200px', minWidth: 0 }}
              />
            </Box>
          ) : cfg.footerNote ? (
            <Typography sx={footerNoteSx}>{cfg.footerNote(finding, archived)}</Typography>
          ) : null}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={onClose}>Close</Button>
            {cfg.mutate && (
              <Button onClick={save} variant="contained" disabled={saving}>
                {saving ? 'Saving…' : saveLabel}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
